import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { BannerProvider, StatusBanner } from '@xingternal/banner';
import { isServer } from '@xingternal/hops';
import { trackEvent } from '@xingternal/hops-tracking';
import { IconButterflyMono, IconNotificationCenter } from '@xingternal/icons';

import { ClientOnly } from '@/components/_common';
import ApplyAction from '@/components/posting-header-actions/components/apply-action';
import { BookmarkAction } from '@/components/posting-header-actions/components/bookmark-action';
import MeatballMenu from '@/components/posting-header-actions/components/meatball-menu';
import { useIsExternalApplicationFlowEnabled } from '@/components/posting-header-actions/use-is-external-application-flow-enabled';
import { JobAlertContext, JobDetailContext } from '@/contexts';
import { JobFeatures, useJobCreateApplyIntentionMutation } from '@/graphql/generated';
import { trackNwtEvent } from '@/helpers/nwt-tracking';
import { useBreakpoints, useIsLoApplyEnabled, useLoginStates, useLoginUrl, useOneClickApplyExperiment } from '@/hooks';

import { OneClickApplyActions } from './one-click-apply-actions';

import * as S from './actions-component.styles';

const nonLoggedInTrackActionByType = {
  EmailApplication: 'jobs_click_application_form_email',
  JobXingApplication: 'jobs_apply_start',
  MessageApplication: 'jobs_click_application_form_xing_message',
  UrlApplication: 'jobs_click_application_form_url',
};

const applicationTypeNwtContext = {
  EmailApplication: 'email_apply',
  JobXingApplication: 'instant_apply',
  MessageApplication: 'private_message_apply',
  UrlApplication: 'url_apply',
};

type Props = {
  features?: JobFeatures;
  pdfUrl?: string;
  applicationType?: string;
  didUserXingApplyLoggedIn?: boolean;
  didUserXingApplyLoggedOut?: boolean;
  applyUrl?: string;
  isPaidJob?: boolean;
  isSticky: boolean;
};

export const Actions: React.FC<Props> = ({
  pdfUrl,
  features,
  applicationType,
  didUserXingApplyLoggedIn,
  didUserXingApplyLoggedOut,
  applyUrl,
  isPaidJob = false,
  isSticky,
}) => {
  const { toggleAlertLightbox } = useContext(JobAlertContext);
  const { id, slug } = useContext(JobDetailContext);
  const { isMobile } = useBreakpoints();
  const { isLoggedIn, isLoggedOut } = useLoginStates();
  const loginUrl = useLoginUrl();
  const { isLoApplyEnabled, receivedVariant } = useIsLoApplyEnabled();
  const { isExternalApplicationFlowEnabled, externalApplicationFlowTrackingProps } =
    useIsExternalApplicationFlowEnabled(isPaidJob);
  const { isExperimentActive: isOneClickApplyExperimentActive } = useOneClickApplyExperiment();
  const intl = useIntl();

  const isXingApplication = applicationType === 'JobXingApplication';
  const isUrlApplication = applicationType === 'UrlApplication' && applyUrl;

  // As isLoApplyEnabled experiment check is only needed for LO flow, there's no need to wait for the async experiments call to be loaded because it's being done before due to LO SSR */
  const isLoXingApplication = isXingApplication && isLoApplyEnabled;
  const isApplyButtonHidden = (isLoggedIn && didUserXingApplyLoggedIn) || (!isLoggedIn && didUserXingApplyLoggedOut);

  const [jobCreateApplyIntentionMutation] = useJobCreateApplyIntentionMutation({
    variables: {
      id,
    },
  });

  const openAlertLightbox = (): void => {
    toggleAlertLightbox({ actionOrigin: 'jobs_menu' });
  };

  const trackNonLoggedInApply = (): void => {
    if (applicationType && nonLoggedInTrackActionByType[applicationType]) {
      const trackingData = {
        PropTrackAction: nonLoggedInTrackActionByType[applicationType],
        PropJobsApplyId: uuidv4(),
        ...(isXingApplication && { PropExperiment: `ABACUS-127|A|${receivedVariant}` }),
        ...(isUrlApplication && isLoggedOut && externalApplicationFlowTrackingProps),
      };
      trackEvent('PropTrackAction', trackingData);
      trackNwtEvent({
        channel: isMobile ? 'webm' : 'web',
        event: 'intended',
        intention: 'apply',
        page: 'jobs/detail',
        item_urn: `surn:x-xing:jobs:posting:${id}`,
        element: 'apply_button',
        context: applicationTypeNwtContext[applicationType],
      });
    }
  };

  const handleNonLoggedInApply = async (): Promise<void> => {
    trackNonLoggedInApply();
    await jobCreateApplyIntentionMutation();

    if (!isServer && !isUrlApplication) {
      window.location.assign(loginUrl);
    }
  };

  const testId = isXingApplication ? 'apply-button-with-butterfly-icon-not-li' : 'apply-button-not-li';
  const applyButtonLabel = isUrlApplication
    ? 'JOB_POSTING_APPLY_EXTERNAL_URL'
    : 'JOB_POSTING_APPLY_CRAWLED_BUTTON_FORM_ACTION_LIST';
  const urlApplicationProps =
    isExternalApplicationFlowEnabled && isLoggedOut
      ? { to: `/jobs/${slug}/external-application` }
      : { href: applyUrl, target: '_blank' };
  const applyButton =
    isLoggedIn || isLoXingApplication ? (
      <BannerProvider Component={StatusBanner} defaultProps={{ display: 'fixed', variant: 'error' } as const}>
        {/* isPaidJob prop is needed for ABACUS-420 */}
        <ApplyAction isSticky={isSticky} isPaidJob={isPaidJob} />
      </BannerProvider>
    ) : (
      <S.StyledApplyAction
        $isSticky={isSticky}
        variant="primary"
        size="medium"
        icon={isXingApplication && IconButterflyMono}
        onClick={handleNonLoggedInApply}
        data-testid={testId}
        data-cy={testId}
        {...(isUrlApplication && urlApplicationProps)}
      >
        <FormattedMessage id={applyButtonLabel} />
      </S.StyledApplyAction>
    );

  const showOneClickApplyActions =
    isLoggedIn && isXingApplication && isOneClickApplyExperimentActive && !didUserXingApplyLoggedIn;

  return (
    <S.ActionsComponentContainer $isSticky={isSticky}>
      {showOneClickApplyActions ? (
        <OneClickApplyActions isSticky={isSticky} />
      ) : (
        <>
          {!isApplyButtonHidden && applyButton}
          {isLoggedOut ? (
            <ClientOnly>
              <S.StyledSearchAlertButton
                variant="secondary"
                size="medium"
                onClick={openAlertLightbox}
                icon={IconNotificationCenter}
                aria-label={intl.formatMessage({ id: 'JOBS_POSTING_ACTIONS_CREATE_ALERT' })}
                data-cy="create-alert-button-lo"
                data-testid="create-alert-button-lo"
                $isSticky={isSticky}
              >
                {!isMobile && !isSticky && <FormattedMessage id="JOBS_POSTING_ACTIONS_CREATE_ALERT" />}
              </S.StyledSearchAlertButton>
              {pdfUrl && <MeatballMenu pdfUrl={pdfUrl} />}
            </ClientOnly>
          ) : (
            <>
              <BookmarkAction isSticky={isSticky} />
              {isMobile && <MeatballMenu pdfUrl={pdfUrl} features={features} />}
            </>
          )}
        </>
      )}
    </S.ActionsComponentContainer>
  );
};
